import { getRandomGenieDefaultImage } from "@/data/genie/defaults";
import { supabase } from "@/integrations/supabase/client";
import { SetupAnalysisResult } from "@/types/agent/setup";
import { DEFAULT_BRANDING } from "@/types/ui/branding";
import { extractColorPalette, suggestTextColors } from "@/utils/image/colorExtraction";
import { VisionPrompt } from "./prompts/VisionPrompt";
import { ResponseParser } from "./utils/ResponseParser";

export class ImageAnalyzer {
  /**
   * Analyzes an image (as base64 data URL) and extracts genie setup parameters
   */
  public async analyzeImage(imageDataUrl: string): Promise<SetupAnalysisResult> {
    try {
      console.log("Starting vision analysis...");

      // Extract base64 data from data URL
      const base64Data = imageDataUrl.includes(",") ? imageDataUrl.split(",")[1] : imageDataUrl;

      // Call the vision edge function
      const { data, error } = await supabase.functions.invoke("analyze-image-vision", {
        body: {
          imageData: base64Data,
          prompt: VisionPrompt.getVisionAnalysisPrompt(),
          temperature: 0.3,
          max_tokens: 1500,
        },
      });

      if (error) {
        throw new Error(`Vision analysis failed: ${error.message}`);
      }

      if (!data || !data.response) {
        throw new Error("Invalid response from vision analysis: no response data");
      }

      console.log("Raw vision response:", data.response);

      const cleanContent = ResponseParser.extractJsonFromResponse(data.response);
      console.log("Extracted JSON:", cleanContent);

      const analysis = JSON.parse(cleanContent);

      console.log("Parsed analysis:", analysis);

      // Generate branding from analysis
      const defaultImage = getRandomGenieDefaultImage();
      let brandingToPreview = {
        ...DEFAULT_BRANDING,
        customAvatarUrl: defaultImage,
        logoUrl: defaultImage,
      };

      // Use extracted colors from vision analysis if available
      if (analysis.extractedColors?.primary) {
        const suggestedPrimary = analysis.extractedColors.primary;
        const suggestedSecondary = analysis.extractedColors.secondary || suggestedPrimary;
        const { primaryText } = suggestTextColors(suggestedPrimary);

        brandingToPreview = {
          ...brandingToPreview,
          primaryColor: suggestedPrimary,
          secondaryColor: suggestedSecondary,
          gradientEnabled: true,
          textColor: primaryText,
          qrCodeStyle: {
            ...(brandingToPreview.qrCodeStyle || DEFAULT_BRANDING.qrCodeStyle!),
            foregroundColor: suggestedPrimary,
          },
          embedStyle: {
            ...(brandingToPreview.embedStyle || DEFAULT_BRANDING.embedStyle!),
            headerColor: suggestedPrimary,
          },
        };
      } else {
        // Extract colors from the default image if vision didn't provide colors
        try {
          const { suggestedPrimary, suggestedSecondary } = await extractColorPalette(defaultImage);
          const { primaryText } = suggestTextColors(suggestedPrimary);

          brandingToPreview = {
            ...brandingToPreview,
            primaryColor: suggestedPrimary,
            secondaryColor: suggestedSecondary,
            gradientEnabled: true,
            textColor: primaryText,
            qrCodeStyle: {
              ...(brandingToPreview.qrCodeStyle || DEFAULT_BRANDING.qrCodeStyle!),
              foregroundColor: suggestedPrimary,
            },
            embedStyle: {
              ...(brandingToPreview.embedStyle || DEFAULT_BRANDING.embedStyle!),
              headerColor: suggestedPrimary,
            },
          };
        } catch (err) {
          console.warn("Color extraction failed; using defaults:", err);
        }
      }

      // Return analysis result matching SetupAnalysisResult structure
      return {
        genieName: analysis.genieName || "My Genie",
        useCase: analysis.useCase || "General assistance",
        language: analysis.language || "en",
        voicePreference: analysis.voicePreference || "friendly and professional",
        conversationSettings: {
          maxDuration: analysis.conversationSettings?.maxDuration || 1800,
          turnTimeout: analysis.conversationSettings?.turnTimeout || 7,
          silenceTimeout: analysis.conversationSettings?.silenceTimeout || 300,
          turnMode: analysis.conversationSettings?.turnMode || "silence",
        },
        systemPrompt: analysis.systemPrompt || `You are ${analysis.genieName || "a helpful assistant"}.`,
        firstMessage: analysis.firstMessage || "Hello! How can I help you today?",
        webWelcomeMessage: analysis.webWelcomeMessage || `Hey! I'm your ${analysis.genieName || "assistant"}`,
        confidence: Math.max(0, Math.min(1, analysis.confidence || 0.5)),
        customerEmail: analysis.customerEmail || undefined,

        // Business context
        brand: analysis.brand || undefined,
        category: analysis.category || undefined,
        description: analysis.description || undefined,
        supportEmails: analysis.supportEmails?.filter((email: string) => email && email.trim()) || undefined,
        sendConversationReports: analysis.sendConversationReports ?? true,

        // Technical parameters
        llmModel: analysis.llmModel || "gemini-3-flash-preview",
        temperature: analysis.temperature !== undefined ? Math.max(0, Math.min(1, analysis.temperature)) : 0.7,
        maxTokens: analysis.maxTokens || 8000,
        textOnly: false,

        // Voice parameters
        voiceStability: analysis.voiceStability !== undefined ? Math.max(0, Math.min(1, analysis.voiceStability)) : 0.5,
        voiceSimilarity:
          analysis.voiceSimilarity !== undefined ? Math.max(0, Math.min(1, analysis.voiceSimilarity)) : 0.8,
        speechSpeed: analysis.speechSpeed !== undefined ? Math.max(0.5, Math.min(2.0, analysis.speechSpeed)) : 1.0,

        // Goal information
        goal: analysis.goal || undefined,

        // Branding with extracted or default colors
        branding: brandingToPreview,

        // Empty arrays for fields not applicable from image
        knowledgeBase: [],
        pronunciations: [],
        dynamicVariables: undefined,
        attachPhoneNumber: false,
        isoCountryCode: "US",
        sendSandboxLink: false,
        sendDemoLink: false,
        sendPricingData: false,
      };
    } catch (error) {
      console.error("Image analysis failed:", error);

      // Return minimal default configuration
      return {
        genieName: "My Genie",
        useCase: "General assistance",
        language: "en",
        voicePreference: "friendly and professional",
        conversationSettings: {
          maxDuration: 1800, // 30 minutes
          turnTimeout: 7,
          silenceTimeout: 300,
        },
        systemPrompt: "You are a helpful assistant.",
        firstMessage: "Hello! How can I help you today?",
        webWelcomeMessage: "Hey! I'm your My Genie",
        confidence: 0.1,
      };
    }
  }
}
